/* This module co-locates the hero-skinned currency PROVIDER + its glyph components with the pure resolver
   helpers/hooks (they share one source of truth); opt out of the refresh-only rule like the sibling
   theme-data modules (cosmetics-theme.tsx, WardrobeTabBar.tsx). */
/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, type FC, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { HEROES } from '@kidshome/hero-content';
import { tPlayKey } from '@/shared/lib/i18n';

/**
 * The hero-SKINNED effort currency (ADR-0103 decision 9). The ✨ "stars" currency is the *hero's* — each hero
 * declares its own display `name` (i18n key) + `icon` in its content (`currency`), so a princess kid sees
 * "stars" ⭐ and a robot kid sees "credits" + a chip. This resolves the active hero's pair (with a canonical
 * ✨ fallback when no hero is chosen / a hero authors none). COINS are the shared allowance and are NEVER
 * skinned — they keep `CoinIcon`/`CoinChip` everywhere. Lives in `entities/hero` (the hero owns its identity);
 * the wardrobe + every ✨ surface import the resolved glyph + name from here.
 */

/** The effort-star (✨) glyph — the canonical "stars" icon (princess + the no-hero fallback). */
export function StarGlyph({ size = 12 }: { size?: number }): React.ReactElement {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" aria-hidden="true">
      <path
        d="M12 2.6l2.6 6.5 7 .5-5.4 4.5 1.7 6.8L12 17.7 6.1 21.4l1.7-6.8L2.4 9.6l7-.5z"
        fill="currentColor"
      />
    </svg>
  );
}

/** The robot "credits" glyph — a little microchip (proves the ✨ currency is hero-skinned, not a fixed star). */
export function ChipGlyph({ size = 12 }: { size?: number }): React.ReactElement {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" aria-hidden="true">
      <rect x="6.5" y="6.5" width="11" height="11" rx="2.4" fill="currentColor" />
      <rect x="9.5" y="9.5" width="5" height="5" rx="1" fill="var(--surface, #fff)" />
      {/* pins */}
      <g stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
        <path d="M9 6.5V4M12 6.5V4M15 6.5V4M9 17.5V20M12 17.5V20M15 17.5V20" />
        <path d="M6.5 9H4M6.5 12H4M6.5 15H4M17.5 9H20M17.5 12H20M17.5 15H20" />
      </g>
    </svg>
  );
}

/** The knight "valor" glyph — a heraldic shield crest (proves the ✨ currency is hero-skinned, not a fixed star). */
export function ShieldGlyph({ size = 12 }: { size?: number }): React.ReactElement {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" aria-hidden="true">
      <path d="M12 2.5l7 2.4v6.1c0 4.2-3 7.2-7 8.5-4-1.3-7-4.3-7-8.5V4.9z" fill="currentColor" />
      <path
        d="M12 7.2l1.3 2.7 2.9.3-2.2 2 .7 2.9L12 15.7l-2.4 1.4.7-2.9-2.2-2 2.9-.3z"
        fill="var(--surface, #fff)"
      />
    </svg>
  );
}

/** The canonical ✨ skin — the fallback when there is no active hero (onboarding / degraded). */
const CANONICAL_CURRENCY = { nameKey: 'play.currency.star', iconRef: 'star' } as const;

/** Map a hero's `currency.iconRef` (a content slug) to its web glyph. Unknown refs fall back to the star. */
const CURRENCY_GLYPHS: Record<string, FC<{ size?: number }>> = {
  star: StarGlyph,
  chip: ChipGlyph,
  shield: ShieldGlyph,
};

export interface HeroCurrency {
  /** i18n key for the currency's display name (e.g. `play.currency.princess`). */
  readonly nameKey: string;
  /** The resolved glyph component for the currency's icon. */
  readonly Icon: FC<{ size?: number }>;
}

/**
 * Resolve the effort-currency skin (name key + glyph) for the given active hero, or the canonical ✨ when
 * `archetypeId` is null / unknown. Pure — reads the compiled `HEROES` catalog (each hero's authored `currency`).
 */
export function heroCurrencyFor(archetypeId: string | null | undefined): HeroCurrency {
  const hero = archetypeId ? HEROES.find((h) => h.archetypeId === archetypeId) : undefined;
  const currency = hero?.currency ?? CANONICAL_CURRENCY;
  return {
    nameKey: currency.nameKey,
    Icon: CURRENCY_GLYPHS[currency.iconRef] ?? StarGlyph,
  };
}

/**
 * The active hero's archetype for the ✨-currency skin, scoped to a surface (e.g. the wardrobe). `null` →
 * the canonical ✨. A context (not prop-threading) so every ✨ glyph under it — the balance pill, the legend,
 * the save ring, each tile's price — skins consistently from ONE place, without each sub-component taking a prop.
 */
const HeroCurrencyContext = createContext<string | null>(null);

/** Scope the ✨-currency skin to its children (the wardrobe passes the kid's active hero). */
export function HeroCurrencyProvider({
  archetypeId,
  children,
}: {
  archetypeId: string | null;
  children: ReactNode;
}): React.ReactElement {
  return (
    <HeroCurrencyContext.Provider value={archetypeId}>{children}</HeroCurrencyContext.Provider>
  );
}

/** The resolved ✨-currency skin (name key + glyph) for the surrounding {@link HeroCurrencyProvider}. */
export function useHeroCurrency(): HeroCurrency {
  return heroCurrencyFor(useContext(HeroCurrencyContext));
}

/** The active hero's ✨ glyph — reads the surrounding currency context (canonical ✨ outside a provider). */
export function CurrencyGlyph({ size }: { size?: number }): React.ReactElement {
  const { Icon } = useHeroCurrency();
  return <Icon size={size} />;
}

/**
 * The hero's currency NAME, translated (e.g. "stars" / "credits") — for TEXT that names the ✨ currency
 * (reactions, "N to next", "Spend N", "Save to unlock"). The skinned counterpart to {@link CurrencyGlyph}:
 * a string can't embed the glyph, so currency strings interpolate this name instead of a hardcoded ✨.
 * `archetypeId` null/unknown → the canonical "stars".
 */
// `t` mirrors `tPlayKey`'s param (the sanctioned `any`-param escape hatch this fn delegates to). A
// narrower `(key: string, …)` here forces tsc to structurally relate the heavily-overloaded `TFunction`
// key-type against `string`, blowing the type-instantiation depth limit (an internal compiler crash).
export function heroCurrencyName(
  t: Parameters<typeof tPlayKey>[0],
  archetypeId: string | null | undefined,
  count?: number,
): string {
  // With a count, i18next picks the CLDR plural form of the name (`…star_one/_few/_many/_other`) so the
  // noun AGREES with the number it sits next to (uk: "1 зірка / 2 зірки / 5 зірок"; en: "1 star / 2 stars").
  // Count-less resolves the base key — the invariant label ("stars" / "зірки") for legends and notes.
  return tPlayKey(
    t,
    heroCurrencyFor(archetypeId).nameKey,
    count === undefined ? undefined : { count },
  );
}

/** Hook form of {@link heroCurrencyName} for an explicit active hero (surfaces outside a provider). */
export function useHeroCurrencyName(archetypeId: string | null | undefined): string {
  const { t } = useTranslation('play');
  return heroCurrencyName(t, archetypeId);
}

/**
 * Per-count resolver form of {@link useHeroCurrencyName} — for surfaces that interpolate the currency name
 * into COUNT phrases ("Spend {{count}} {{currency}}"), where the name must decline with each phrase's number.
 */
export function useHeroCurrencyNameFor(
  archetypeId: string | null | undefined,
): (count: number) => string {
  const { t } = useTranslation('play');
  return (count) => heroCurrencyName(t, archetypeId, count);
}

/** The currency NAME for the surrounding {@link HeroCurrencyProvider} (the wardrobe) — pairs with CurrencyGlyph. */
export function useScopedCurrencyName(): string {
  const { t } = useTranslation('play');
  return tPlayKey(t, useHeroCurrency().nameKey);
}

/** Per-count resolver form of {@link useScopedCurrencyName} — the declined name for a count phrase. */
export function useScopedCurrencyNameFor(): (count: number) => string {
  const { t } = useTranslation('play');
  const { nameKey } = useHeroCurrency();
  return (count) => tPlayKey(t, nameKey, { count });
}
